import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { InputSearchSelect } from '@/design_system/InputSearchSelect/InputSearchSelect';
import { Label, LabelProps } from '@/design_system/Label/Label';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import { useCreateUniqueArticle, useUniqueArticles } from '@/models/uniqueArticle';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import useDebouncedState from '@/utils/useDebouncedState';

type UniqueArticleOption = {
  id: string;
  serialNumber: string;
};

export const UniqueArticleSearchSelect = (props: {
  labelProps: LabelProps;
  value: UniqueArticleOption | null;
  onChange: (option: UniqueArticleOption | null) => void;
  ariaLabel?: string;
  placeholder?: string;
  isReplacementSerialNumber?: boolean;
  error?: string;
  productId?: string;
  brandId?: string;
  isDisabled?: boolean;
  tooltipLabel?: string;
}) => {
  const { labelProps, tooltipLabel } = props;

  if (tooltipLabel) {
    return (
      <Label {...labelProps}>
        <Tooltip content={tooltipLabel}>
          <Button variant="style-less">
            <UniqueArticleSearchSelectContent {...props} />
          </Button>
        </Tooltip>
      </Label>
    );
  }

  return (
    <Label {...labelProps}>
      <UniqueArticleSearchSelectContent {...props} />
    </Label>
  );
};

const UniqueArticleSearchSelectContent = ({
  value,
  onChange,
  ariaLabel,
  placeholder,
  isReplacementSerialNumber = false,
  error,
  productId,
  brandId,
  isDisabled,
}: {
  value: UniqueArticleOption | null;
  onChange: (option: UniqueArticleOption | null) => void;
  ariaLabel?: string;
  placeholder?: string;
  isReplacementSerialNumber?: boolean;
  error?: string;
  productId?: string;
  brandId?: string;
  isDisabled?: boolean;
}) => {
  const { article, view } = useArticleContext();

  const [search, debouncedSearch, setSearch] = useDebouncedState(value?.serialNumber ?? '', 500);

  const { mutateAsync: createUniqueArticle, isPending: isCreateUniqueArticlePending } =
    useCreateUniqueArticle(article.requestId);

  const {
    data: { uniqueArticles } = { uniqueArticles: [] },
    isFetching: isUniqueArticlesFetching,
  } = useUniqueArticles(
    {
      serialNumber: debouncedSearch,
      productId,
      brandId,
      limit: 10,
    },
    {
      keepPreviousData: false,
      enabled: !!debouncedSearch,
    }
  );

  const showAddUniqueArticleOption =
    view.details.serialNumber.canCreate &&
    !uniqueArticles.length &&
    !isUniqueArticlesFetching &&
    search === debouncedSearch;

  const addUniqueArticleOption = {
    id: 'add',
    serialNumber: '',
  };

  const uniqueArticleAndAddOptions = [
    ...uniqueArticles,
    showAddUniqueArticleOption ? addUniqueArticleOption : undefined,
  ].filter((option) => option !== undefined);

  const handleCreateUniqueArticle = async (uniqueArticleOption: UniqueArticleOption | null) => {
    if (uniqueArticleOption?.id === 'add') {
      if (search !== '') {
        await createUniqueArticle({
          articleId: article.id,
          serialNumber: search,
          isReplacementSerialNumber,
        });
      }
    } else {
      onChange(uniqueArticleOption);
    }

    setSearch('');
  };

  return (
    <InputSearchSelect
      variant="search"
      aria-label={ariaLabel}
      placeholder={placeholder}
      value={value}
      options={uniqueArticleAndAddOptions}
      getOptionValue={(option) => option.serialNumber}
      getOptionLabel={(option) => option.serialNumber}
      formatOptionLabel={(option) => {
        if (option?.id === 'add') {
          return (
            <Stack gap="0.25rem" row>
              <Trans id="article.form.unique-article.no-results">No results found.</Trans>

              {search !== '' && (
                <span style={{ textDecoration: 'underline' }}>
                  <Trans id="article.form.unique-article.add">
                    Add "{search}" as a new serial number
                  </Trans>
                </span>
              )}
            </Stack>
          );
        } else {
          return <>{option?.serialNumber}</>;
        }
      }}
      inputValue={search}
      onInputChange={(newSearch) => setSearch(newSearch.trim().toUpperCase())}
      blurInputOnSelect
      filterOption={null} // Avoid react-select to filter options only based on their label
      onChange={handleCreateUniqueArticle}
      isLoading={
        isUniqueArticlesFetching || isCreateUniqueArticlePending || search !== debouncedSearch
      }
      style={{ flex: 1, textAlign: 'left' }}
      isDisabled={isDisabled}
      error={error}
    />
  );
};
